import { Link } from 'react-router-dom';
import { useEffect } from 'react';
import { ArrowRight, Leaf, Heart, Users, Target } from 'lucide-react';
import { Button } from '../components/ui/button';

export const About = () => {
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const values = [
    {
      icon: Leaf,
      title: "Authenticity",
      description: "We source only the purest herbs and follow traditional Ayurvedic preparation methods passed down through generations."
    },
    {
      icon: Heart,
      title: "Care",
      description: "Every product is crafted with love and attention, ensuring the highest quality for our customers' well-being."
    },
    {
      icon: Users,
      title: "Community",
      description: "We work closely with local farmers and practitioners to preserve and promote Ayurvedic knowledge."
    },
    {
      icon: Target,
      title: "Excellence",
      description: "Our state-of-the-art facilities and rigorous quality control ensure consistently effective products."
    }
  ];

  const milestones = [
    { year: "1990", event: "Amjatha Pharma founded by P.K.M. Aboobacker" },
    { year: "1995", event: "Began manufacturing own formulations" },
    { year: "2000", event: "Patented 8 Ayurvedic tonics" },
    { year: "2010", event: "Adaloda-EX becomes a trusted household name" },
    { year: "2020", event: "Infrastructure modernization begins" },
    { year: "2024", event: "Expanding to overseas markets" },
  ];

  return (
    <div data-testid="about-page" className="min-h-screen bg-[#FAF9F6] pt-24">
      {/* Hero Section */}
      <section className="relative py-16 md:py-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#F2F0E9] to-[#FAF9F6]"></div>
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#C69C6D]/10 rounded-full blur-3xl"></div>
        
        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-6 space-y-6">
              <span className="text-sm font-semibold tracking-widest uppercase text-[#C69C6D]">
                Our Story
              </span>
              <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-[#1C1C1C] leading-tight">
                The Journey of <span className="text-[#B91C1C]">Amjatha Pharma</span>
              </h1>
              <p className="text-lg text-[#57534E] leading-relaxed">
                From ancestral wisdom to modern manufacturing, from humble beginnings to a future-ready enterprise—rooted in tradition, driven by purpose, and dedicated to people.
              </p>
            </div>
            
            <div className="lg:col-span-6">
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-br from-[#C69C6D]/20 to-[#2D4A3E]/10 rounded-3xl"></div>
                <div className="relative grid grid-cols-2 gap-3 rounded-2xl overflow-hidden">
                  {/* Forest Path */}
                  <div className="relative aspect-square overflow-hidden rounded-xl">
                    <img
                      src="https://images.unsplash.com/photo-1760044604355-458eef15ac64?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwyfHxmb3Jlc3QlMjBwYXRoJTIwd2Fsa3dheSUyMHRhbGwlMjB0cmVlcyUyMGdyZWVuJTIwY2Fub3B5JTIwdHJhaWx8ZW58MHx8fHwxNzcwMjkwMDI2fDA&ixlib=rb-4.1.0&q=85"
                      alt="Forest walkway through tall trees"
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  {/* Raw Materials - Ayurvedic */}
                  <div className="relative aspect-square overflow-hidden rounded-xl">
                    <img
                      src="https://images.pexels.com/photos/5480036/pexels-photo-5480036.jpeg"
                      alt="Ayurvedic herbs preparation"
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  {/* Traditional Manufacturing */}
                  <div className="relative aspect-square overflow-hidden rounded-xl">
                    <img
                      src="https://images.unsplash.com/photo-1652960018678-1f19799996c5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODh8MHwxfHNlYXJjaHwxfHxicmFzcyUyMGNvcHBlciUyMHV0ZW5zaWxzJTIwdHJhZGl0aW9uYWwlMjBJbmRpYW4lMjB2ZXNzZWxzfGVufDB8fHx8MTc3MDI4OTYwNXww&ixlib=rb-4.1.0&q=85"
                      alt="Traditional brass vessels for manufacturing"
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  {/* Trust */}
                  <div className="relative aspect-square overflow-hidden rounded-xl">
                    <img
                      src="https://images.unsplash.com/photo-1638262052640-82e94d64664a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHw0fHxoYW5kc2hha2UlMjB0cnVzdCUyMGJ1c2luZXNzJTIwcGFydG5lcnNoaXB8ZW58MHx8fHwxNzcwMjg5NjA5fDA&ixlib=rb-4.1.0&q=85"
                      alt="Trusted partnership"
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-8">
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#1C1C1C] mb-8">
                Our Journey
              </h2>
              <div className="prose prose-lg max-w-none text-[#57534E] space-y-6">
                <p>
                  Amjatha Pharma was founded in 1990 by P.K.M. Aboobacker, a man whose life journey is deeply rooted in the legacy of Ayurveda and a steadfast commitment to society.
                </p>
                
                <p>
                  Born into the renowned Peringattuthodiyil Ayurvedic family, Aboobacker inherited a lineage of healing wisdom that spans generations. The family was widely respected for its Ayurvedic Bishagwarwas, with historical accounts tracing their reputation back to the era of Tipu Sultan. Physicians from this lineage are believed to have treated wounded soldiers during that period, establishing a lasting cultural and historical connection between Ayurveda, Irimbiluyam Panchayat, and this family of healers.
                </p>
                
                <p>
                  Although surrounded by traditional medicine from an early age and closely observing the practice run by his father and uncle, circumstances did not permit Aboobacker to pursue formal medical education. Like many young men of Kerala in the 1980s, he sought livelihood abroad and spent nearly eight years working in Bahrain. Yet, even while overseas, his connection to Ayurveda remained unwavering. Choosing purpose over comfort, he made the uncommon decision to return to Kerala at a time when foreign employment was widely seen as the ultimate measure of success.
                </p>
                
                <p>
                  Upon his return, entrepreneurship itself was still uncommon in Kerala. Guided by a clear vision to serve society through authentic Ayurvedic medicine, Aboobacker began by establishing a distribution firm supplying Ayurvedic formulations to practitioners and patients across the region. Over time, growing concerns about the quality and authenticity of medicines available in the market prompted a decisive step—to manufacture medicines he could personally stand behind.
                </p>
                
                <h3 className="font-heading text-2xl font-semibold text-[#1C1C1C] mt-8 mb-4">
                  The Early Days
                </h3>
                
                <p>
                  The early days of Amjatha Pharma were defined by humility and determination. With limited resources, operations began with just one experienced staff member—Shankaran, a trusted assistant who had worked closely with Aboobacker's father, the late Kunhidru Vaidyar. This continuity of hands and knowledge ensured that the principles of traditional practice remained intact from the very beginning.
                </p>
                
                <h3 className="font-heading text-2xl font-semibold text-[#1C1C1C] mt-8 mb-4">
                  The Name Behind the Brand
                </h3>
                
                <p>
                  The name Amjatha Pharma itself reflects the deep personal values behind the institution. The name was lovingly suggested by Aboobacker's wife, Kadeejakutty, who also belongs to the Peringattuthodiyil family and is the daughter of a respected Ayurvedic physician. Amjatha is the name of their youngest daughter, chosen by her brothers, Aboobacker's two sons. The name Amjatha carries connotations of purity and excellence, reflecting the values the family wished the institution to embody.
                </p>
                
                <h3 className="font-heading text-2xl font-semibold text-[#1C1C1C] mt-8 mb-4">
                  Growth & Recognition
                </h3>
                
                <p>
                  Gradually, Amjatha Pharma expanded its capabilities by patenting eight Ayurvedic tonics and obtaining licenses to manufacture a comprehensive range of Ayurvedic formulations. Through perseverance, uncompromising quality standards, and deep respect for tradition, the company steadily earned the trust of practitioners and families alike.
                </p>
                
                <p>
                  Today, Amjatha Pharma stands as a trusted name, where the strength and effectiveness of its products speak louder than the brand itself. A notable example is Adaloda-EX, a cough syrup that has grown into a widely recognized over-the-counter product, trusted across generations.
                </p>
                
                <h3 className="font-heading text-2xl font-semibold text-[#1C1C1C] mt-8 mb-4">
                  Looking Forward
                </h3>
                
                <p>
                  While remaining firmly rooted in the principles of Ayurveda, Amjatha Pharma is now undergoing significant infrastructural transformation to remain resilient in a rapidly evolving world. The company is actively working toward modernizing and automating its infrastructure, adopting intelligent systems that enhance quality, consistency, and scalability. Equal importance is placed on the well-being and growth of its workforce, recognizing that people remain the foundation of any enduring institution. With a forward-looking vision, Amjatha Pharma is committed to upholding the dignity of Ayurveda while steadily expanding its presence into overseas markets.
                </p>
                
                <div className="mt-10 p-8 bg-[#F2F0E9] rounded-2xl border-l-4 border-[#B91C1C]">
                  <p className="font-accent italic text-xl text-[#1C1C1C] mb-0">
                    "From ancestral wisdom to modern manufacturing, from humble beginnings to a future-ready enterprise, the journey of Amjatha Pharma stands as a testament to resilience, heritage, and a lifelong devotion to healing—rooted in tradition, driven by purpose, and dedicated to people."
                  </p>
                </div>
              </div>
            </div>
            
            <div className="lg:col-span-4">
              <div className="sticky top-32">
                <h3 className="font-heading text-2xl font-semibold text-[#1C1C1C] mb-6">
                  Milestones
                </h3>
                <div className="space-y-6">
                  {milestones.map((milestone, index) => (
                    <div 
                      key={index}
                      data-testid={`milestone-${index}`}
                      className="flex items-start gap-4"
                    >
                      <div className="flex-shrink-0 w-16 text-right">
                        <span className="font-heading text-xl font-bold text-[#B91C1C]">
                          {milestone.year}
                        </span>
                      </div>
                      <div className="flex-shrink-0 flex flex-col items-center">
                        <div className="w-3 h-3 rounded-full bg-[#2D4A3E]"></div>
                        {index < milestones.length - 1 && (
                          <div className="w-0.5 h-12 bg-[#E7E5E4]"></div>
                        )}
                      </div>
                      <p className="text-[#57534E] pt-0.5">{milestone.event}</p>
                    </div>
                  ))}
                </div>
                
                {/* Founder Card */}
                <div className="mt-12 p-6 bg-[#F2F0E9] rounded-2xl">
                  <h4 className="font-heading text-lg font-semibold text-[#1C1C1C] mb-2">
                    Founder
                  </h4>
                  <p className="text-[#B91C1C] font-semibold text-lg mb-2">P.K.M. Aboobacker</p>
                  <p className="text-[#57534E] text-sm">
                    Born into the Peringattuthodiyil Ayurvedic family, with a lineage of healing wisdom spanning generations.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-16 md:py-24 bg-[#F2F0E9]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="text-sm font-semibold tracking-widest uppercase text-[#C69C6D] mb-4 block">
              What We Stand For
            </span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#1C1C1C]">
              Our Core Values
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div 
                key={value.title}
                data-testid={`value-${index}`}
                className="group p-8 bg-white rounded-2xl border border-[#E7E5E4] hover:border-[#C69C6D]/30 transition-all duration-300 hover:shadow-lg"
              >
                <div className="w-14 h-14 mb-6 rounded-xl bg-[#2D4A3E]/10 flex items-center justify-center group-hover:bg-[#2D4A3E] transition-colors">
                  <value.icon className="w-7 h-7 text-[#2D4A3E] group-hover:text-white transition-colors" strokeWidth={1.5} />
                </div>
                <h3 className="font-heading text-xl font-semibold text-[#1C1C1C] mb-3">
                  {value.title}
                </h3>
                <p className="text-[#57534E] text-sm leading-relaxed">
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Certifications */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#1C1C1C] mb-4">
              Quality Assurance
            </h2>
            <p className="text-lg text-[#57534E] max-w-2xl mx-auto">
              Our commitment to quality is backed by recognized certifications
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center p-8 bg-[#FAF9F6] rounded-2xl">
              <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-[#2D4A3E]/10 flex items-center justify-center">
                <span className="font-heading text-2xl font-bold text-[#2D4A3E]">GMP</span>
              </div>
              <h3 className="font-heading text-xl font-semibold text-[#1C1C1C] mb-2">
                GMP Certified
              </h3>
              <p className="text-[#57534E] text-sm">
                Good Manufacturing Practices ensuring consistent quality
              </p>
            </div>
            
            <div className="text-center p-8 bg-[#FAF9F6] rounded-2xl">
              <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-[#2D4A3E]/10 flex items-center justify-center">
                <span className="font-heading text-2xl font-bold text-[#2D4A3E]">ISO</span>
              </div>
              <h3 className="font-heading text-xl font-semibold text-[#1C1C1C] mb-2">
                ISO 9001:2015
              </h3>
              <p className="text-[#57534E] text-sm">
                International standard for quality management systems
              </p>
            </div>
            
            <div className="text-center p-8 bg-[#FAF9F6] rounded-2xl">
              <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-[#2D4A3E]/10 flex items-center justify-center">
                <span className="font-heading text-xl font-bold text-[#2D4A3E]">AYUSH</span>
              </div>
              <h3 className="font-heading text-xl font-semibold text-[#1C1C1C] mb-2">
                AYUSH Approved
              </h3>
              <p className="text-[#57534E] text-sm">
                Recognized by the Ministry of AYUSH, Government of India
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#2D4A3E] relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-[#C69C6D] rounded-full blur-3xl"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to Experience Authentic Ayurveda?
          </h2>
          <p className="text-lg text-white/80 max-w-xl mx-auto mb-8">
            Connect with us to learn more about our products and how they can support your wellness journey.
          </p>
          <Link to="/contact">
            <Button 
              data-testid="about-cta-btn"
              className="bg-[#C69C6D] text-white hover:bg-[#B0885A] rounded-full px-10 py-6 text-lg transition-all duration-300 shadow-lg hover:-translate-y-1"
            >
              Get in Touch
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};
